import { useTranslation } from 'react-i18next';
import type { AccountStatus } from '@hero/schema';
import { Badge } from '../../../ui/Badge/Badge';

export interface AccountStatusBadgeProps {
  status: AccountStatus;
}

/**
 * Status pill for a bilateral `Account`. Mirrors
 * `TransactionStatusBadge` / `InvoiceStatusBadge` in shape — colour
 * + label only, no icon, because the only three statuses currently
 * map cleanly to neutral / warning / muted tones.
 *
 * Kept colour mapping inline (instead of `getXxxBadgeClass` in
 * `@hero/web-theme`) because the surface is tiny and the three
 * tokens here aren't reused anywhere else; adding a theme helper
 * would be ceremony for one component.
 */
export function AccountStatusBadge({ status }: AccountStatusBadgeProps) {
  const { t } = useTranslation('m-account');

  const STYLE_BY_STATUS: Record<AccountStatus, string> = {
    active: 'bg-green-50 text-green-700 border-green-200',
    suspended: 'bg-amber-50 text-amber-700 border-amber-200',
    in_review: 'bg-blue-50 text-blue-700 border-blue-200',
  };

  return (
    <Badge variant="secondary" size="default" className={STYLE_BY_STATUS[status]}>
      {t(`status.${status}`, { defaultValue: status })}
    </Badge>
  );
}
